import React, { useEffect, useRef } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import { Calendar, X, DollarSign, Mail, Phone, Building2, MapPin, FileText, Tag, Hash, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {CATEGORY_OPTIONS} from '../../Constants/constants';


const STATUS_OPTIONS = [
    'Available', 'In Use', 'Under Repair', 'Retired', 'Lost'
];

// Helper function to map backend status values to translation keys
const getStatusTranslationKey = (status) => {
    const statusMap = {
        'Available': 'assets.statusAvailable',
        'In Use': 'assets.statusInUse',
        'Under Repair': 'assets.statusUnderRepair',
        'Retired': 'assets.statusRetired',
        'Lost': 'assets.statusLost'
    };
    return statusMap[status] || status;
};

/**
 * AssetForm component using uncontrolled inputs to prevent focus issues
 */
const AssetForm = ({ show, onHide, mode, asset, onSubmit }) => {
    const { t } = useTranslation('assets');
    // Create a ref for the form
    const formRef = useRef(null);

    // Populate form with asset data when modal opens
    useEffect(() => {
        if (show && formRef.current) {
            const form = formRef.current;

            // Timeout allows DOM to settle before filling values
            setTimeout(() => {
                if (mode === 'create') {
                    // Reset form for new creation
                    form.reset();

                    // Set default values
                    const statusElement = form.elements.status;
                    if (statusElement) {
                        statusElement.value = t('available');
                    }
                }
                else if (asset) {
                    // Fill form with asset data
                    Object.keys(asset).forEach(key => {
                        const formElement = form.elements[key];
                        if (formElement) {
                            if (key === 'acquisition_date' && asset[key]) {
                                // Format date to YYYY-MM-DD
                                formElement.value = new Date(asset[key]).toISOString().split('T')[0];
                            } else {
                                formElement.value = asset[key] !== null ? asset[key] : '';
                            }
                        }
                    });
                }
            }, 0);
        }
    }, [show, asset, mode]);

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();

        // Get form data
        const formData = new FormData(formRef.current);
        const assetData = {};

        // Convert FormData to object
        for (const [key, value] of formData.entries()) {
            assetData[key] = value;
        }

        // Process special fields
        assetData.acquisition_date = assetData.acquisition_date || null;
        assetData.price = assetData.price ? parseFloat(assetData.price) : 0;

        // Submit the data
        onSubmit(assetData);
    };

    const isViewMode = mode === 'view';

    // Form field component
    const FormField = ({ label, name, type = "text", icon, required, children }) => {
        if (isViewMode && asset) {
            let displayValue = asset[name] || '-';

            if (type === 'date' && asset[name]) {
                displayValue = new Date(asset[name]).toLocaleDateString();
            } else if (type === 'number' && name === 'price') {
                displayValue = `$${parseFloat(asset[name] || 0).toFixed(2)}`;
            }

            return (
                <Form.Group className="mb-4">
                    <Form.Label className="d-flex align-items-center text-muted mb-2 small">
                        {icon && <span className="me-2">{icon}</span>}
                        {label}
                    </Form.Label>
                    <div className="form-control-plaintext fs-6 fw-medium">
                        {displayValue}
                    </div>
                </Form.Group>
            );
        }

        return (
            <Form.Group className="mb-4">
                <Form.Label className="d-flex align-items-center mb-2 small">
                    {icon && <span className="me-2 text-primary opacity-75">{icon}</span>}
                    {label} {required && <span className="text-danger ms-1">{t('assetForm.required')}</span>}
                </Form.Label>
                {children || (
                    <Form.Control
                        type={type}
                        name={name}
                        defaultValue=""
                        required={required}
                        className="shadow-none border-2"
                        autoComplete="off"
                    />
                )}
            </Form.Group>
        );
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            dialogClassName="modal-large"
            centered
            className="asset-form-modal"
            size="lg"
            backdrop="static"
        >
            <Modal.Header className="border-bottom-0 pb-0 px-4 pt-4">
                <Modal.Title>
                    <h2 className="mb-1 fw-bold">
                        {mode === 'create' ? t('assetForm.addNewAsset') :
                            mode === 'edit' ? t('assetForm.editAsset') :
                                t('assetForm.assetDetails')}
                    </h2>
                    <p className="text-muted small mb-0">
                        {mode === 'create' ? t('assetForm.addNewAssetDescription') :
                            mode === 'edit' ? t('assetForm.editAssetDescription') :
                                t('assetForm.viewAssetDescription')}
                    </p>
                </Modal.Title>
                <Button
                    variant="link"
                    onClick={onHide}
                    className="p-0 ms-2 text-muted border-0"
                    aria-label={t('common.close')}
                >
                    <X size={20} />
                </Button>
            </Modal.Header>

            <Modal.Body className="px-4 py-3">
                <Form ref={formRef} id="assetForm" onSubmit={handleSubmit} noValidate>
                    <Row className="gx-4 gy-2">
                        <Col md={6}>
                            <FormField
                                label={t('assets.assetType')}
                                name="asset_type"
                                icon={<Tag size={18} />}
                                required
                            >
                                <Form.Select
                                    name="asset_type"
                                    defaultValue=""
                                    required
                                    className="shadow-none border-2"
                                >
                                    <option value="">{t('assetForm.selectAssetType')}</option>
                                    {CATEGORY_OPTIONS.map(option => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </Form.Select>
                            </FormField>
                        </Col>
                        <Col md={6}>
                            <FormField
                                label={t('assets.brand')}
                                name="brand"
                                icon={<Tag size={18} />}
                                required
                            />
                        </Col>
                        <Col md={6}>
                            <FormField
                                label={t('assets.model')}
                                name="model"
                                icon={<Tag size={18} />}
                                required
                            />
                        </Col>
                        <Col md={6}>
                            <FormField
                                label={t('assets.serialNumber')}
                                name="serial_number"
                                icon={<Hash size={18} />}
                                required
                            />
                        </Col>
                        <Col md={6}>
                            <FormField
                                label={t('assetForm.imei')}
                                name="imei"
                                icon={<Smartphone size={18} />}
                            />
                        </Col>
                        <Col md={6}>
                            <FormField
                                label={t('assets.status')}
                                name="status"
                                icon={<Tag size={18} />}
                                required
                            >
                                <Form.Select
                                    name="status"
                                    defaultValue="Available"
                                    required
                                    className="shadow-none border-2"
                                >
                                    {STATUS_OPTIONS.map(option => (
                                        <option key={option} value={option}>
                                            {t(getStatusTranslationKey(option))}
                                        </option>
                                    ))}
                                </Form.Select>
                            </FormField>
                        </Col>
                        <Col md={6}>
                            <FormField
                                label={t('assetForm.acquisitionDate')}
                                name="acquisition_date"
                                type="date"
                                icon={<Calendar size={18} />}
                            />
                        </Col>
                        <Col md={6}>
                            <FormField
                                label={t('assetForm.price')}
                                name="price"
                                type="number"
                                icon={<DollarSign size={18} />}
                            />
                        </Col>
                        <Col md={6}>
                            <FormField
                                label={t('assetForm.custodianEmail')}
                                name="custodian_email"
                                type="email"
                                icon={<Mail size={18} />}
                            />
                        </Col>
                        <Col md={6}>
                            <FormField
                                label={t('assetForm.phoneContact')}
                                name="phone_contact"
                                icon={<Phone size={18} />}
                            />
                        </Col>
                        <Col md={6}>
                            <FormField
                                label={t('assetForm.office')}
                                name="office"
                                icon={<Building2 size={18} />}
                            />
                        </Col>
                        <Col md={6}>
                            <FormField
                                label={t('assets.location')}
                                name="location"
                                icon={<MapPin size={18} />}
                            />
                        </Col>
                        <Col md={12}>
                            <FormField
                                label={t('assetForm.detailsInformation')}
                                name="details_information"
                                icon={<FileText size={18} />}
                            >
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="details_information"
                                    defaultValue=""
                                    className="shadow-none border-2"
                                    autoComplete="off"
                                />
                            </FormField>
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>

            <Modal.Footer className="border-top-0 px-4 pb-4">
                <Button
                    variant="light"
                    onClick={onHide}
                    className="px-4 py-2"
                >
                    {t('common.cancel')}
                </Button>
                {!isViewMode && (
                    <Button
                        variant="primary"
                        type="submit"
                        form="assetForm"
                        className="px-4 py-2"
                    >
                        {mode === 'create' ? t('assets.createAsset') : t('assets.updateAsset')}
                    </Button>
                )}
            </Modal.Footer>

            <style jsx global>{`
        .modal-large {
          max-width: 80vw;
          width: 80vw;
        }

        .asset-form-modal .modal-content {
          border-radius: 12px;
          border: none;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
        }

        .asset-form-modal .form-control,
        .asset-form-modal .form-select {
          padding: 0.6rem 0.75rem;
          border-radius: 6px;
          transition: all 0.2s ease;
          border-color: #dee2e6;
        }

        .asset-form-modal .form-control:focus,
        .asset-form-modal .form-select:focus {
          border-color: #0d6efd;
          box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
        }

        .asset-form-modal .btn {
          border-radius: 6px;
          font-weight: 500;
        }

        .asset-form-modal .form-label {
          font-weight: 500;
          color: #4a5568;
        }

        .asset-form-modal textarea.form-control {
          min-height: 100px;
        }
      `}</style>
        </Modal>
    );
};

export default AssetForm;